import {Component, HostBinding, Inject, LOCALE_ID, OnInit} from '@angular/core';
import {UnitLogManagerService} from '../../unit-log-manager/unit-log-manager.service';
import {formatDate} from '@angular/common';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {NotificationsService} from 'angular2-notifications';
import {delay, map} from 'rxjs/operators';
import {AuthorizationService} from '../../authorization/authorization.service';
import {RouteLibraryService} from '../../route/route-library.service';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {WebStompService} from '../../web-stomp/web-stomp.service';
import {AlarmLogModel} from '../../dashboard/monitoring/alarm-log.model';
import {combineLatest, Observable, of} from 'rxjs';
import {timestampToFullDateTime} from '../../utility/timestamp-functions';
import {RouteLibraryEntities} from '../../route/route-library-entity';

interface AlarmLogItem {
  activeAlarm: AlarmLogModel;
  formGroup: FormGroup;
  expanded: boolean;
  removed: boolean;
}

@Component({
  selector: 'app-unit-log-popover',
  templateUrl: './unit-log-popover.component.html',
})
export class UnitLogPopoverComponent implements OnInit {
  protected readonly RouteLibraryEntities = RouteLibraryEntities;
  @HostBinding('class.header-menu-item') alarmNotificationsContainer = true;
  popupIsOpen = false;
  public readonly alarmTypeUnit = UnitLogManagerService.alarmTypeUnit;
  public readonly alarmTypeEndUser = UnitLogManagerService.alarmTypeEndUser;
  formIsVisible = false;
  activeFormIndex = undefined;
  public items: Array<AlarmLogItem> = [];
  public processedComment: {formGroup: FormGroup} = {formGroup: null};
  public showCloseAllForm = false;
  public notRemovedItems: Array<AlarmLogItem> = [];
  public notificationLinkText: string;
  popupItems = [
    {
      title: 'Units event log',
      iconName: 'ic-log',
      routerLink: '/notification/unit-log-list',
      visible: of(this.authorizationService.canAccessSharingFeatures()),
    },
    {
      title: 'Log of notifications by clients',
      iconName: 'ic-log',
      routerLink: '/notification/end-user-log-list',
      visible: combineLatest([
        this.authorizationService.canAccessSharingFeatures(),
        this.authorizationService.loggedInUserHasRightsNonLessThanDispatcher()
      ]).pipe(map(([acc1, acc2]) => acc1 && acc2)),
    },
    {
      title: 'Customize notifications by unit',
      iconName: 'ic-settings',
      routerLink: '/notification/notification-list',
      visible: of(this.authorizationService.canAccessSharingFeatures()),
    },
    {
      title: 'Customize notifications by client',
      iconName: 'ic-settings',
      routerLink: '/notification/end-user-notification-list',
      visible: combineLatest([
        this.authorizationService.canAccessSharingFeatures(),
        this.authorizationService.loggedInUserHasRightsNonLessThanDispatcher()
      ]).pipe(map(([acc1, acc2]) => acc1 && acc2)),
    },
  ];

  constructor(
    public unitLogManagerService: UnitLogManagerService,
    protected formBuilder: FormBuilder,
    protected notif: NotificationsService,
    protected authorizationService: AuthorizationService,
    public routeLibrary: RouteLibraryService,
    private router: Router,
    private translate: TranslateService,
    protected webStompService: WebStompService,
    @Inject(LOCALE_ID) protected localeId: string
  ) { }

  ngOnInit() {
    this.translate.get('More').subscribe(text => this.notificationLinkText = text);
    this.unitLogManagerService.activeAlarms.subscribe(
      (activeAlarms) => {
        const activeAlarmIndex = activeAlarms.reduce(
          (prev, activeAlarm) => {
            prev[activeAlarm.id] = true;
            return prev;
          },
          {}
        );
        const itemIndex = this.items.reduce(
          (prev, item) => {
            prev[item.activeAlarm.id] = true;
            return prev;
          },
          {}
        );
        this.items.forEach(
          (item, index) => {
            if (!activeAlarmIndex.hasOwnProperty(item.activeAlarm.id)) {
              this.items[index].removed = true;
            }
          }
        );
        const newItems = [];
        let needToPlayAlarmSound = false;
        for (let alarmIndex = activeAlarms.length - 1; alarmIndex >= 0; alarmIndex--) {
          const activeAlarm = activeAlarms[alarmIndex];
          if (!itemIndex.hasOwnProperty(activeAlarm.id)) {
            newItems.unshift({
              activeAlarm: activeAlarm,
              formGroup: this.formBuilder.group({
                processed_comment: ['', Validators.required]
              }),
              expanded: true,
              removed: false,
            });

            const notif = this.notif.error(
              '', this.getNotificationHtml(activeAlarm),
              {
                timeOut: 0,
                id: this.unitLogManagerService.getAlarmNotificationKey(activeAlarm.type, activeAlarm.id),
                clickToClose: false,
                html: true,
                theClass: 'custom-alarm-notification'
              }
            );

            notif.click.subscribe(event => {
              if (event.target.classList) {
                if (event.target.classList.contains('notification-link')) {
                  switch (activeAlarm.type) {
                    case UnitLogManagerService.alarmTypeUnit:
                      this.router.navigateByUrl(
                        this.routeLibrary.getRoute({
                          entity: RouteLibraryEntities.UNIT_LOG,
                          action: 'view',
                          id: activeAlarm.id
                        })
                      );
                      break;
                    case UnitLogManagerService.alarmTypeEndUser:
                      this.router.navigateByUrl(
                        this.routeLibrary.getRoute({
                          entity:  RouteLibraryEntities.END_USER_LOG,
                          action: 'view',
                          id: activeAlarm.id
                        })
                      );
                  }
                } else if (event.target.classList.contains('notification-item-link')) {
                  switch (activeAlarm.type) {
                    case UnitLogManagerService.alarmTypeUnit:
                      this.router.navigateByUrl(
                        this.routeLibrary.getRoute({
                          entity: RouteLibraryEntities.UNIT,
                          action: 'view',
                          id: activeAlarm.item_id
                        })
                      );
                      break;
                    case UnitLogManagerService.alarmTypeEndUser:
                      this.router.navigateByUrl(
                        this.routeLibrary.getRoute({
                          entity:  RouteLibraryEntities.END_USER,
                          action: 'view',
                          id: activeAlarm.item_id
                        })
                      );
                  }
                }
              }
              this.notif.remove(notif.id);
            });
            needToPlayAlarmSound = true;
          }
          this.items.forEach((i, index) => {
            if (i.activeAlarm.id === activeAlarm.id) {
              this.items[index].activeAlarm.retranslator_id = activeAlarm.retranslator_id;
            }
          });
        }
        this.items = [...newItems, ...this.items];

        this.notRemovedItems = this.items.filter((item, index) => {
          return this.items[index].removed !== true;
        });
        if (needToPlayAlarmSound) {
          try {
            const audio = new Audio();
            audio.src = '/assets/audio/alarm.wav';
            audio.load();
            audio.play();
          } catch (e) {
            console.error(e);
          }
        }
      }
    );

    this.webStompService.alarmClosed
      .subscribe(
        alarmClosed => {
          const index = this.items.findIndex(item => item.activeAlarm.type === alarmClosed.type && item.activeAlarm.id === alarmClosed.id);
          this.items[index].removed = true;
          this.notif.remove(this.unitLogManagerService.getAlarmNotificationKey(alarmClosed.type, alarmClosed.id));
        }
      );
    this.processedComment = {
      formGroup: this.formBuilder.group({
        processed_comment: ['', Validators.required]
      })
    };
  }
  formatItemDate(timestamp) {
    return timestampToFullDateTime(timestamp);
  }

  openForm(index, item) {
    item.formGroup.reset();
    item.formGroup.markAsUntouched();
    this.formIsVisible = true;
    this.activeFormIndex = index;
  }

  closeForm(item) {
    item.formGroup.reset();
    item.formGroup.markAsUntouched();
    this.formIsVisible = false;
    this.activeFormIndex = undefined;
  }

  onSubmit(item: AlarmLogItem) {
    if (item.formGroup.valid) {
      // let closeAlarmObservable: Observable<any>;
      // switch (item.activeAlarm.type) {
      //   case UnitLogManagerService.alarmTypeUnit:
      //     closeAlarmObservable = this.unitLogManagerService.closeUnitAlarm(
      //       item.activeAlarm.id, item.formGroup.controls.processed_comment.value
      //     );
      //     break;
      //   case UnitLogManagerService.alarmTypeEndUser:
      //
      // }
      const subscription =
        this.unitLogManagerService.closeAlarm(
          item.activeAlarm.type,
          item.activeAlarm.id,
          item.formGroup.controls.processed_comment.value
        ).pipe(
          delay(0)
        ).subscribe(
          () => subscription.unsubscribe()
        );
      this.closeForm(item);
    } else {
      item.formGroup.controls.processed_comment.markAsTouched({onlySelf: true});
    }
  }

  closeAllAlarmClick(popover) {
    this.showCloseAllForm = true;
    popover.toggle();
  }

  closeModal(...popover) {
    popover.map(el => el.close());
  }

  onCloseAll(comment) {
    if (comment.formGroup.valid) {
      const subscription = this.unitLogManagerService
      .closeAllWithComment(comment.formGroup.controls.processed_comment.value)
      .pipe(delay(0))
      .subscribe(
        () => {
          subscription.unsubscribe();
        }
      );
      this.showCloseAllForm = false;
    } else {
      comment.formGroup.controls.processed_comment.markAsTouched({onlySelf: true});
    }
  }
  closeAllButtonIsAvailableToUser(): Observable<boolean> {
    return this.authorizationService.loggedInUserHasRightsNonLessThanDispatcher();
  }

  toggleRetranslator(item: AlarmLogModel) {
    // console.log(item);
    if (item.retranslator_id) {
      this.unitLogManagerService.stopRetranslator(item.retranslator_id)
        .subscribe();
    } else {
      this.unitLogManagerService.createRetranslator(item.retranslator_setting_id, item.id)
        .subscribe();
    }

  }

  private getNotificationHtml(activeAlarm: AlarmLogModel): string {
    return `<div class="custom-notification-container">
              <div class="custom-notification-title">${activeAlarm.notification_name}</div>
              <div class="custom-notification-body">
                <span class="notification-item-link custom-notification-item-name">
                  ${activeAlarm.item_name}
                </span>: ${activeAlarm.description}
              </div>
              <div class="custom-notification-footer">
                <div class="custom-notification-date">${formatDate(activeAlarm.timestamp * 1000, 'medium', this.localeId)}</div>
                <div class="notification-link custom-notification-btn">${this.notificationLinkText}</div>
              </div>
            </div>`;
  }
}
